import type { CharacterOperation } from './operationTypes';
import type { MarketingDecision } from './marketingTypes';
import type { PlatformDecision, PlatformId } from './platformTypes';
import type { TodayDecision } from './types';
import type { ManagerExplanation } from './explainTypes';

/**
 * Explain Templates — 固定文案模板（全白话）
 *
 * 只按已决字段选择模板，不重新计算 ratio / fatigue / 平台。
 * 输出不得出现工程字眼。
 */

const BANNED_TERMS = [
  'Marketing',
  'Platform',
  'cooldown',
  'promotion_allowed',
  'trust_ratio',
  'commercial_ratio',
  'today_content_mode',
  'TodayDecision',
  'visibility_mode',
  'promotion_level',
  'marketing_mode',
  'marketing_priority',
];

/** 用户可见平台名：仅中国平台；海外 ID 不露出英文品牌 */
const PLATFORM_CN: Partial<Record<PlatformId, string>> = {
  xiaohongshu: '小红书',
};

function scrubPlain(text: string): string {
  let out = text;
  for (const term of BANNED_TERMS) {
    out = out.split(term).join('');
  }
  // 二次清洗：若上游 reason 夹带海外品牌，用户可见层一律去掉
  for (const brand of ['Instagram', 'Threads', 'Facebook', 'TikTok', 'YouTube', 'LinkedIn', 'Pinterest', 'Twitter']) {
    out = out.split(brand).join('');
  }
  return out.replace(/\s{2,}/g, ' ').trim();
}

function joinReasons(parts: string[]): string {
  return parts.map(scrubPlain).filter(Boolean).join('\n');
}

function platformCnName(id: PlatformId | null): string {
  if (!id) return '';
  return PLATFORM_CN[id] || '';
}

/** 只读已决字段，输出白话平台提醒（不 dump platform_reason；海外平台用不点名说法） */
function platformHint(platform: PlatformDecision): string {
  if (platform.visibility_mode === 'cooldown' || !platform.primary_platform) {
    return '今天先不主动经营任何平台';
  }
  const primary = platformCnName(platform.primary_platform);
  const secondary = platform.secondary_platforms
    .map(platformCnName)
    .filter(Boolean);
  if (primary && secondary.length > 0) {
    return `今天重点放在${primary}，可顺带兼顾${secondary.join('、')}`;
  }
  if (primary) {
    return `今天重点放在${primary}`;
  }
  return '今天按既定重点平台节奏经营，先把互动做稳';
}

function materialHint(decision: TodayDecision): string {
  const enabled = (Object.keys(decision.materials) as Array<keyof typeof decision.materials>)
    .filter((k) => decision.materials[k].enabled)
    .map((k) => {
      if (k === 'photo') return '照片';
      if (k === 'voice') return '配音';
      if (k === 'talking_video') return '口播';
      if (k === 'short_video') return '短片';
      return '';
    })
    .filter(Boolean);
  if (enabled.length === 0) return '今天不安排新素材';
  return `今天素材侧重：${enabled.join('、')}`;
}

function promotionHint(marketing: MarketingDecision): string {
  if (!marketing.promotion_allowed || marketing.promotion_level === 'none') {
    return '今天先别推产品';
  }
  if (marketing.promotion_level === 'light') return '今天可以轻推，保持自然';
  return '今天可以自然介绍，但仍别逼单';
}

/** 白话原因：只取营运/营销人话，不用平台原始 reason（可能含工程字） */
function plainReason(
  operation: CharacterOperation,
  marketing: MarketingDecision,
  platform: PlatformDecision,
): string {
  return joinReasons([
    ...operation.today_reason,
    ...marketing.promotion_reason,
    platformHint(platform),
  ]);
}

function plainWarning(
  platform: PlatformDecision,
  marketing: MarketingDecision,
  decision: TodayDecision,
): string {
  return joinReasons([
    platformHint(platform),
    promotionHint(marketing),
    materialHint(decision),
  ]);
}

type TemplateBuilder = (
  operation: CharacterOperation,
  marketing: MarketingDecision,
  platform: PlatformDecision,
  decision: TodayDecision,
) => ManagerExplanation;

const restTemplate: TemplateBuilder = (operation, marketing, platform, decision) => ({
  today_summary: '今天安排休息与恢复，主要目的不是曝光，而是让角色状态回稳。',
  today_goal: '恢复精力，避免透支',
  today_reason: plainReason(operation, marketing, platform),
  today_usage: '今天不主动推送内容。若有旧帖互动，只做轻量回应即可。',
  today_do: ['✓ 休息不硬推', '✓ 轻量回覆既有留言（可选）', '✓ 观察状态是否回稳'],
  today_dont: ['✗ 不要硬发生成素材', '✗ 不要强推产品', '✗ 不要跨平台连发'],
  today_expected_result: ['降低疲劳感', '避免商业过载', '为后续互动保留空间'],
  today_next_step: '状态回稳后，明天可回到信任日或互动日。',
  today_warning: plainWarning(platform, marketing, decision),
  today_value: '今天不是成交。今天是在保护长期经营节奏。',
});

const trustTemplate: TemplateBuilder = (operation, marketing, platform, decision) => ({
  today_summary: '今天安排生活照，主要目的不是曝光，而是降低商业感。',
  today_goal: '建立信任，增加陌生留言机会',
  today_reason: plainReason(operation, marketing, platform),
  today_usage: '发布后，优先回覆留言。不要急着介绍产品。',
  today_do: ['✓ 回留言', '✓ 收藏有温度的留言', '✓ 与粉丝聊天'],
  today_dont: ['✗ 不要一直贴链接', '✗ 不要一直推产品', '✗ 不要硬广话术'],
  today_expected_result: ['提高留言率', '建立聊天机会', '增加粉丝信任'],
  today_next_step: '若今天留言增加，明天可进入互动日。',
  today_warning: plainWarning(platform, marketing, decision),
  today_value: '今天不是成交。今天是在替未来成交铺路。',
});

const interactionTemplate: TemplateBuilder = (operation, marketing, platform, decision) => ({
  today_summary: '今天安排轻互动与回应，主要目的不是推产品，而是把关系做热。',
  today_goal: '增加互动与对话',
  today_reason: plainReason(operation, marketing, platform),
  today_usage: '把时间花在回覆、提问与聊天上。用对话代替硬发内容。',
  today_do: ['✓ 优先回覆留言与私信', '✓ 主动提问引发对话', '✓ 收藏高意向对话'],
  today_dont: ['✗ 不要只发不回', '✗ 不要急着导流成交', '✗ 不要刷屏推链接'],
  today_expected_result: ['提高回覆率', '增加对话深度', '积累可经营关系'],
  today_next_step: '互动升温后，后续才适合轻量自然推广。',
  today_warning: plainWarning(platform, marketing, decision),
  today_value: '今天不是成交。今天是在把流量变成关系。',
});

const promotionTemplate: TemplateBuilder = (operation, marketing, platform, decision) => ({
  today_summary: '今天安排轻量自然推广，主要目的是顺势介绍，而不是硬推逼单。',
  today_goal: '轻量推广，保持自然感',
  today_reason: plainReason(operation, marketing, platform),
  today_usage: '发布后先观察互动。有兴趣再顺着聊，不要开场就强推。',
  today_do: ['✓ 用生活语境带出价值', '✓ 回覆兴趣留言', '✓ 保留软性引导'],
  today_dont: ['✗ 不要硬广连发', '✗ 不要只贴购买链接', '✗ 不要忽视信任基础'],
  today_expected_result: ['自然露出产品价值', '筛选高意向互动', '维持人设可信度'],
  today_next_step: '若推广反馈偏冷，下一轮先回到信任日或互动日。',
  today_warning: plainWarning(platform, marketing, decision),
  today_value: '今天可以轻推。但仍以信任为底，不为短期成交牺牲长期感。',
});

const knowledgeTemplate: TemplateBuilder = (operation, marketing, platform, decision) => ({
  today_summary: '今天安排知识分享，主要目的是建立专业感，而不是直接成交。',
  today_goal: '知识输出，建立专业信任',
  today_reason: plainReason(operation, marketing, platform),
  today_usage: '用可懂的口吻分享一个小知识点。引导提问，而不是硬推方案。',
  today_do: ['✓ 讲清一个小重点', '✓ 邀请提问', '✓ 回覆相关疑问'],
  today_dont: ['✗ 不要堆砌术语', '✗ 不要夹带硬广', '✗ 不要一次讲太多'],
  today_expected_result: ['提升专业信任', '引发提问', '沉淀可回访内容'],
  today_next_step: '若提问增多，下一步可转互动日或轻量推广。',
  today_warning: plainWarning(platform, marketing, decision),
  today_value: '今天不是成交。今天是在用专业感替未来铺路。',
});

const fallbackTemplate: TemplateBuilder = (operation, marketing, platform, decision) => ({
  today_summary: '今天按基础经营节奏推进，主要目的是保持稳定出现，而不是强推转化。',
  today_goal: '维持基础经营节奏',
  today_reason: plainReason(operation, marketing, platform),
  today_usage: '按今天的重点平台节奏发布与回覆。先稳，再谈加码。',
  today_do: ['✓ 完成今日基础经营动作', '✓ 回覆关键留言', '✓ 记录互动反馈'],
  today_dont: ['✗ 不要临时加码硬推', '✗ 不要偏离人设', '✗ 不要忽略回覆'],
  today_expected_result: ['保持稳定出现', '维持基础互动', '为下一轮安排留参考'],
  today_next_step: '观察今日反馈后，再决定明天走信任、互动或轻推。',
  today_warning: plainWarning(platform, marketing, decision),
  today_value: '今天是在维持经营底盘。稳定比冲动更重要。',
});

/**
 * 仅按已决 marketing_mode 选模板（翻译，非重新决策）。
 */
export function selectExplainTemplate(
  marketing: MarketingDecision,
): TemplateBuilder {
  switch (marketing.marketing_mode) {
    case 'rest':
      return restTemplate;
    case 'trust':
      return trustTemplate;
    case 'interaction':
      return interactionTemplate;
    case 'promotion':
    case 'campaign':
      return marketing.promotion_allowed ? promotionTemplate : trustTemplate;
    case 'knowledge':
      return knowledgeTemplate;
    default:
      return fallbackTemplate;
  }
}

/** 导出 scrub，供 Explain 保底文案复用 */
export function scrubExplainText(text: string): string {
  return scrubPlain(text);
}
